import { useCallback } from 'react';
import type { DeliveryOrderDetail } from '@sg/graphql-schema';

import { useOrdering } from '@order/features/ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type PartialLineItem } from '../../../../graphql';
import { getTips } from '../../utils';
import { useAddUpsellToBag } from '../useAddUpsellToBag';
import { useBagLoyaltyData } from '../useBagLoyaltyData';
import { useBagLoyaltyRewards } from '../useBagLoyaltyRewards';
import { useBagPaymentInfo } from '../useBagPaymentInfo';
import { useBagTracking } from '../useBagTracking';
import { useCart } from '../useCart';
import { useChangeChannelOrLocation } from '../useChangeChannelOrLocation';
import { useCredits } from '../useCredits';
import { useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { useFetchTelemetryData } from '../useFetchTelemetryData';
import { useFetchWantedTimes } from '../useFetchWantedTimes';
import { useGiftCards } from '../useGiftCards';
import { useNavigatePostOrder } from '../useNavigatePostOrder';
import { useOnPostOrder } from '../useOnPostOrder';
import { usePaymentMethods } from '../usePaymentMethods';
import { usePromoCode } from '../usePromoCode';
import { useRequestLogin } from '../useRequestLogin';
import { useRewards } from '../useRewards';
import { useSubmitOrder } from '../useSubmitOrder';
import { useUpdateLineItem } from '../useUpdateLineItem';

/**
 * Hook that binds the ordering machine with the external data.
 */
export const useOrderState = (params: UseOrderStateParams) => {
  const { orderSubmissionDelay } = params;

  const isLoggedIn = useIsLoggedIn();
  const bagTracking = useBagTracking();
  const dynamicTips = useFeatureFlag('CELS-1191-permanent-dynamic-tip-values');
  const wantedTimesPollingInterval = useFeatureFlag('permanent-bag-wanted-times-polling-interval-milliseconds'); // prettier-ignore

  // ─── Loyalty ──────────────────────────────────────────────────────────────

  const { fetchLoyaltyData } = useBagLoyaltyData();
  const { fetchRewards } = useBagLoyaltyRewards();
  const { applyReward, removeReward } = useRewards();

  // ──────────────────────────────────────────────────────────────────────────

  const { fetchCart } = useCart();
  const { fetchCredits } = useCredits();
  const { fetchWantedTimes } = useFetchWantedTimes();
  const { addUpsellToBag } = useAddUpsellToBag();
  const { fetchGiftCardBalance, applyGiftCard } = useGiftCards(fetchCredits);
  const { fetchBagPaymentInfo } = useBagPaymentInfo();
  const { fetchPaymentMethods } = usePaymentMethods();
  const { fetchDefaultPaymentMethod, setDefaultPaymentMethod } =
    useDefaultPaymentMethod();
  const { updateLineItem } = useUpdateLineItem();
  const { applyPromoCode } = usePromoCode(fetchRewards);
  const { changeChannelOrLocation } = useChangeChannelOrLocation();
  const { requestLogin } = useRequestLogin();
  const { submitOrder } = useSubmitOrder(setDefaultPaymentMethod);
  const { fetchTelemetryData } = useFetchTelemetryData();
  const { navigatePostOrder } = useNavigatePostOrder();
  const { onPostOrder } = useOnPostOrder();

  const fetchInitialData = useCallback(async () => {
    if (!isLoggedIn) {
      return {
        cart: await fetchCart(),
        paymentMethods: [],
        defaultPaymentMethodId: undefined,
        defaultTip: 0,
        availablePoints: 0,
        availableCredit: 0,
        giftCardBalance: 0,
        selectedTip: 0,
        availableTips: [],
        customerId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        stripeCustomerId: '',
        stripeCustomerKey: '',
        stripeSession: '',
        isNewCustomer: true,
        isOptedOutFromLoyalty: false,
      };
    }

    const [
      cart,
      paymentMethods,
      defaultPaymentMethodId,
      availableCredit,
      giftCardBalance,
      telemetryData,
      loyaltyData,
      bagPaymentInfo,
    ] = await Promise.all([
      fetchCart(),
      fetchPaymentMethods(),
      fetchDefaultPaymentMethod(),
      fetchCredits(),
      fetchGiftCardBalance(),
      fetchTelemetryData(),
      fetchLoyaltyData(),
      fetchBagPaymentInfo(),
    ]);

    const tips = getTips({
      maxTipAmount: cart.maxTipAmount,
      orderChannel: cart.orderChannel,
      dynamicTips,
    });

    return {
      cart,
      paymentMethods,
      defaultPaymentMethodId,
      availableCredit,
      giftCardBalance,
      ...tips,
      ...bagPaymentInfo,
      ...loyaltyData,
      ...telemetryData,
    };
  }, [
    isLoggedIn,
    dynamicTips,
    fetchCart,
    fetchPaymentMethods,
    fetchDefaultPaymentMethod,
    fetchCredits,
    fetchGiftCardBalance,
    fetchTelemetryData,
    fetchLoyaltyData,
    fetchBagPaymentInfo,
  ]);

  return useOrdering<PartialLineItem, DeliveryOrderDetail>({
    isLoggedIn,
    orderSubmissionDelay,
    wantedTimesPollingInterval,
    bagTracking,
    navigatePostOrder,
    fetchInitialData,
    fetchRewards,
    fetchWantedTimes,
    addUpsellToBag,
    updateLineItem,
    changeChannelOrLocation,
    requestLogin,
    applyReward,
    removeReward,
    applyPromoCode,
    applyGiftCard,
    submitOrder,
    onPostOrder,
  });
};

type UseOrderStateParams = {
  orderSubmissionDelay: number;
};
